import { useMemo } from "react"
import { formatDateString } from "@/v2-common/utils/utils.date"
import cn from "@/v2-ui/utils/utils.cn"
import Separator from "@/v2-ui/separator"
import Table, {
  createColumns,
  GROUPING_RANGE_DATES,
  type GroupingRange,
  type TableProps,
} from "@/v2-backoffice-client/components/table"
import { DotText } from "@/v2-backoffice-client/components/dot"
import { useUnlicensedCustomersQuery } from "@/v2-backoffice-client/license/unlicensed/license.unlicensed.query"
import type { UnlicensedCustomerFragment } from "@/v2-backoffice-client/license/unlicensed/__types__/license.unlicensed.fragment"

// The reason buckets, exactly as backoffice.Get_Lic_UnlicensedCustomers
// classifies them — the proc is the source of truth for these strings.
const REASON_LAPSED = "Licenses lapsed"
const REASON_NEVER_LICENSED = "Never licensed"
const REASON_DEAL_NEVER_STARTED = "Deal never started"

// Bands for the 30-day-active count — grouping by them sorts the board into
// "how hot is the gap": a customer with twenty people inside beats a
// single straggler for the next call.
const GROUPING_RANGE_ACTIVE_USERS: GroupingRange[] = [
  { label: "None active", test: (v) => !v || Number(v) <= 0 },
  { label: "1 – 5 active", test: (v) => Number(v) <= 5 },
  { label: "6 – 20 active", test: (v) => Number(v) <= 20 },
  { label: "20+ active", test: () => true },
]

// Same bands over the full user count — how big the exposed org is.
const GROUPING_RANGE_USERS: GroupingRange[] = [
  { label: "No users", test: (v) => !v || Number(v) <= 0 },
  { label: "1 – 5 users", test: (v) => Number(v) <= 5 },
  { label: "6 – 20 users", test: (v) => Number(v) <= 20 },
  { label: "20+ users", test: () => true },
]

function createUnlicensedCustomerColumns() {
  return createColumns<UnlicensedCustomerFragment>({
    enableSorting: true,
    enableHiding: true,
    columns: {
      Customer: {
        id: "Customer",
        label: "Customer",
        defaultSize: 200,
        getValue: (row) => row.CustomerName ?? "",
        renderCell({ row }) {
          const { CustomerName, CustomerId } = row.original
          return CustomerName ?? `#${CustomerId}`
        },
        to: ({ row }) => {
          const externalId = row.original.ExternalCustomerId
          if (!externalId) return
          return `>>?v=customer:${externalId}`
        },
      },
      OrganizationNumber: {
        id: "OrganizationNumber",
        label: "Org Number",
        defaultSize: 110,
        filter: { type: "text", placeholder: "Org number..." },
        getValue: (row) => row.OrganizationNumber ?? "",
      },
      UnlicensedReason: {
        id: "UnlicensedReason",
        label: "Reason",
        description:
          "Licenses lapsed = churned and still using. Never licensed = never sold to. Deal never started = the deal fell through before launch.",
        defaultSize: 150,
        enableGrouping: true,
        filter: {
          type: "multi-select",
          placeholder: "Any reason",
          options: [
            {
              label: REASON_LAPSED,
              value: REASON_LAPSED,
              dotVariant: "destructive",
            },
            {
              label: REASON_NEVER_LICENSED,
              value: REASON_NEVER_LICENSED,
              dotVariant: "warning",
            },
            {
              label: REASON_DEAL_NEVER_STARTED,
              value: REASON_DEAL_NEVER_STARTED,
            },
          ],
        },
        renderCell({ row }) {
          const reason = row.original.UnlicensedReason
          const variant =
            reason === REASON_LAPSED
              ? "destructive"
              : reason === REASON_NEVER_LICENSED
                ? "warning"
                : "muted"
          return <DotText variant={variant}>{reason}</DotText>
        },
      },
      UserCount: {
        id: "UserCount",
        label: "Users",
        description: "Live (non-deleted) users owned by the customer.",
        defaultSize: 70,
        filter: { type: "number-range" },
        groupingRanges: GROUPING_RANGE_USERS,
      },
      ActiveUserCount: {
        id: "ActiveUserCount",
        label: "Active (30d)",
        description:
          "Users with a successful sign-in inside the last 30 days — the board sorts the hottest gaps first.",
        defaultSize: 90,
        filter: { type: "number-range" },
        groupingRanges: GROUPING_RANGE_ACTIVE_USERS,
      },
      LastActivityAt: {
        id: "LastActivityAt",
        label: "Last Activity",
        defaultSize: 100,
        filter: { type: "date-filter" },
        groupingRanges: GROUPING_RANGE_DATES,
        renderCell({ row }) {
          const value = row.original.LastActivityAt
          return value ? formatDateString(value) : null
        },
        exportValue({ row }) {
          return formatDateString(row.original.LastActivityAt)
        },
      },
      LastCoverageEndedAt: {
        id: "LastCoverageEndedAt",
        label: "Coverage Ended",
        description:
          "When the customer's last license ran out — only lapsed customers have one. Group on it to split fresh churn from long-gone freeloaders.",
        defaultSize: 110,
        filter: { type: "date-filter" },
        groupingRanges: GROUPING_RANGE_DATES,
        renderCell({ row }) {
          const value = row.original.LastCoverageEndedAt
          return value ? formatDateString(value) : null
        },
        exportValue({ row }) {
          return formatDateString(row.original.LastCoverageEndedAt)
        },
      },
      PastLicenseCount: {
        id: "PastLicenseCount",
        label: "Past Licenses",
        defaultSize: 90,
        defaultHidden: true,
        filter: { type: "number-range" },
      },
      CustomerCreatedAt: {
        id: "CustomerCreatedAt",
        label: "Customer Since",
        defaultSize: 100,
        defaultHidden: true,
        filter: { type: "date-filter" },
        groupingRanges: GROUPING_RANGE_DATES,
        renderCell({ row }) {
          const value = row.original.CustomerCreatedAt
          return value ? formatDateString(value) : null
        },
        exportValue({ row }) {
          return formatDateString(row.original.CustomerCreatedAt)
        },
      },
      CustomerId: {
        id: "CustomerId",
        label: "Id",
        defaultSize: 50,
        defaultHidden: true,
      },
    },
  })
}

export type LicenseUnlicensedTableProps = Omit<
  TableProps<UnlicensedCustomerFragment>,
  "data" | "columns"
>

// The Unlicensed Usage board (fair share): customers whose people are in the
// app while no license covers them. Review, not enforcement — "this one is
// fine" is recorded as an operator-granted license on the customer panel
// (deep-linked from the Customer column), which removes the row and leaves
// the decision in the license history. Owns the query (shared via the Apollo
// cache with the tab trigger's count).
function LicenseUnlicensedTable(props: LicenseUnlicensedTableProps) {
  const { className, ...tableProps } = props

  const { data, loading, error, refetch } = useUnlicensedCustomersQuery()
  const customers = data?.getUnlicensedCustomers ?? []

  const lapsedCount = useMemo(
    () =>
      customers.filter((row) => row.UnlicensedReason === REASON_LAPSED).length,
    [customers],
  )
  const neverLicensedCount = useMemo(
    () =>
      customers.filter((row) => row.UnlicensedReason === REASON_NEVER_LICENSED)
        .length,
    [customers],
  )

  const columns = useMemo(() => createUnlicensedCustomerColumns(), [])

  return (
    <Table
      className={cn("license-unlicensed-table", className)}
      tableId="license-unlicensed-table"
      data={customers}
      columns={columns}
      enableGrouping
      {...tableProps}
    >
      <Table.Toolbar>
        {/* Namespaced: three boards share the Licensing page, so the default
            "status" param would leak pill state between tabs. */}
        <Table.StatusFilter
          column="UnlicensedReason"
          urlParam="unlicensed-status"
          isLoading={loading}
        >
          <Table.StatusFilter.Option value={null} count={customers.length}>
            All
          </Table.StatusFilter.Option>

          <Table.StatusFilter.Option
            value={0}
            filterValue={REASON_LAPSED}
            variant="destructive"
            iconSrc="exclamation"
            count={lapsedCount}
          >
            Lapsed
          </Table.StatusFilter.Option>

          <Table.StatusFilter.Option
            value={1}
            filterValue={REASON_NEVER_LICENSED}
            count={neverLicensedCount}
          >
            Never Licensed
          </Table.StatusFilter.Option>

          <Table.StatusFilter.Option
            value={2}
            filterValue={REASON_DEAL_NEVER_STARTED}
            count={customers.length - lapsedCount - neverLicensedCount}
          >
            Deal Never Started
          </Table.StatusFilter.Option>
        </Table.StatusFilter>

        <div className="flex-1" />
        <Table.Search column="Customer" placeholder="Search by customer..." />

        <Table.ToolbarFilters />
        <Separator orientation="vertical" />

        <Table.ToolbarMenu
          onRefresh={() => void refetch()}
          exportFileName="unlicensed-usage"
        />
      </Table.Toolbar>

      <Table.FilterList />
      <Table.GroupList />

      <Table.Content isLoading={loading} error={error} />
    </Table>
  )
}

export default LicenseUnlicensedTable
